/**
 * PcmLayoutGuess — what audio is hiding in bytes that carry NO header.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * A WAV whose first sectors were never written (DAW bounce interrupted, data-
 * recovery carve, a "copy" made from a dying disk) is 99.99% intact PCM
 * behind 44 missing bytes. FFmpeg answers "Invalid data found when processing
 * input" to it, and /repair-audio used to answer with the conversion-tool text
 * "try a different output format" — for a file the tool never opened.
 * Measured 2026-09-04: 92 such refusals on .wav in 90 days across both shards
 * (28 intl + 64 RU), the one that prompted this a 967 MB "sad day copy.wav"
 * in Firefox on a Mac.
 *
 * Audio engineers solve this class by hand in Audacity → "Import Raw Data":
 * pick bit depth / endianness / channels / rate, listen, adjust. This module
 * does the picking, because the bytes themselves say which interpretation is
 * audio: real audio is SMOOTH — the energy in sample-to-sample change is a
 * small share of the signal's energy — while every wrong interpretation of the
 * same bytes (wrong width, wrong endianness, wrong channel count, wrong byte
 * alignment) reads as white noise, where that share is ≈ 2.0. See smoothness().
 *
 * One genuine ambiguity has no smoothness answer: 16-bit stereo bytes read as
 * 32-bit mono are equally smooth, because the high half of each 32-bit word IS
 * the right channel. The tie is broken by the low half: in a true 32-bit
 * recording the low 16 bits are converter noise, in a misread stereo file they
 * are a smooth left channel.
 *
 * The other ambiguity is dual-mono: a stereo file whose two channels carry the
 * same signal (one microphone into a stereo track — common) reads as mono with
 * every sample repeated, which is *smoother* than the truth and twice as long.
 * No real recording repeats each sample exactly, so when the reading with
 * twice the channels shows near-identical channel pairs, that is the file.
 *
 * What it does NOT know: the sample rate. Bytes carry no clock. The caller
 * offers 44.1 / 48 / 88.2 / 96 kHz with a preview; `durationSec(rate)` turns
 * the file size into the length each choice implies.
 *
 * ── API ─────────────────────────────────────────────────────────────────────
 *   const g = await PcmLayoutGuess.guess(file, { offset: 0 });
 *   opts.formats / opts.channels restrict the readings tried (an AVI audio
 *   stream is little-endian and 1–2 channels); G.711 ('mulaw', 'alaw') is tried
 *   ONLY when asked for, because the WAV rebuild in /repair-audio does not
 *   write it yet.
 *   g.verdict     'audio' | 'silence' | 'noise'
 *   g.best        { fmt:'s16le', channels:2, bytesPerSample:2, blockAlign:4,
 *                   shift:0, score, label:'16-bit PCM · stereo · little-endian' }
 *   g.candidates  ranked list (same shape), best first
 *   g.durationSec(rate) → seconds for the best layout at that rate
 *
 * `shift` is the byte offset (0…blockAlign-1) at which frames align; the
 * caller starts the PCM slice at `offset + shift`.
 *
 * Verdicts: 'silence' — every window is digital zero: a pre-allocated file
 * that was never written; 'noise' — no layout scores below AUDIO_LIKE
 * anywhere: encrypted / compressed / not PCM. Both are honest "nothing to
 * rebuild here" answers, and both are cheap: the whole verdict costs three
 * 1 MB reads regardless of file size.
 */
(function () {
'use strict';

if (window.PcmLayoutGuess && !window.PcmLayoutGuess.__isStub) return;

var WINDOW_BYTES = 1048576;
/* Every layout is scored on the SAME bytes. Until 2026-09-25 the cap was in
   FRAMES (16 384 per layout), so an 8-channel reading looked at 8× the bytes a
   mono one did: in a window whose first 32 KB are a quiet hiss and whose tail is
   loud, the mono truth scored the hiss and the 8-channel lie scored the music —
   and won ("16-bit 6 ch" for a stereo bounce, "8-bit 8 ch" for beeps). */
var SPAN_BYTES = 131072;
var AUDIO_LIKE = 1.0;                 // Σ(Δx)²/Σ(x−x̄)² below this = a real signal (white noise ≈ 2.0)
/* When the caller already KNOWS the bytes are audio (an AVI 'wb' chunk whose
   frame structure ruled out every compressed codec), a hissy 8 kHz camera track
   at 1.0–1.2 is still audio; only white-noise-like readings are not. */
var KNOWN_AUDIO_LIKE = 1.6;
var SILENT_RMS = 1e-4;

var FORMATS = {
    u8:    { bps: 1, label: '8-bit' },
    mulaw: { bps: 1, label: '8-bit μ-law' }, alaw: { bps: 1, label: '8-bit A-law' },
    s16le: { bps: 2, label: '16-bit' }, s16be: { bps: 2, label: '16-bit' },
    s24le: { bps: 3, label: '24-bit' }, s24be: { bps: 3, label: '24-bit' },
    s32le: { bps: 4, label: '32-bit' }, s32be: { bps: 4, label: '32-bit' },
    f32le: { bps: 4, label: '32-bit float' }, f32be: { bps: 4, label: '32-bit float' }
};
var CHANNELS = [1, 2, 4, 6, 8];
var DEFAULT_FORMATS = ['u8', 's16le', 's16be', 's24le', 's24be', 's32le', 's32be', 'f32le', 'f32be'];

/* G.711 decode tables: telephony / CCTV / old camcorder audio. Without them
   μ-law bytes are scored as the least-bad LINEAR reading — measured on
   camera ambience: "24-bit stereo big-endian" — and rebuilt as noise. */
var MULAW = new Float32Array(256), ALAW = new Float32Array(256);
(function () {
    for (var i = 0; i < 256; i++) {
        var u = ~i & 0xFF, t = ((u & 0x0F) << 3) + 0x84;
        t <<= (u & 0x70) >> 4;
        MULAW[i] = ((u & 0x80) ? (0x84 - t) : (t - 0x84)) / 32768;
        var a = i ^ 0x55, m = (a & 0x0F) << 4, seg = (a & 0x70) >> 4;
        m = seg === 0 ? m + 8 : seg === 1 ? m + 0x108 : (m + 0x108) << (seg - 1);
        ALAW[i] = ((a & 0x80) ? m : -m) / 32768;
    }
})();

/* ── 8-bit: which law ─────────────────────────────────────────────────────
 * u8, μ-law and A-law are the same byte width, and on the 8 kHz camera audio
 * where they live the signal is mostly microphone hiss — smoothness cannot
 * separate them (measured: Fuji S602 u8 1.006 / μ-law 1.006 / A-law 1.007).
 * What does separate them is WHERE the bytes sit: audio amplitudes are roughly
 * Laplacian around zero, and each law quantizes that shape into its own byte
 * histogram (u8 around 0x80, μ-law around 0x7F/0xFF, A-law around 0x55/0xD5).
 * So the law is the one under which "Laplace amplitude, quantized by this law"
 * explains the byte counts best. 21/21 on seven real cameras' audio re-encoded
 * into all three (2026-09-26), the narrowest margin 0.14 nats a sample. */
var LAW_TABS = null;
function lawTables() {
    if (LAW_TABS) return LAW_TABS;
    LAW_TABS = {};
    [['u8', null], ['mulaw', MULAW], ['alaw', ALAW]].forEach(function (e) {
        var tab = new Float64Array(256), i;
        for (i = 0; i < 256; i++) tab[i] = e[1] ? e[1][i] : (i - 128) / 128;
        var order = Array.prototype.slice.call(tab).map(function (v, k) { return k; }).sort(function (a, b) { return tab[a] - tab[b]; });
        var lo = new Float64Array(256), hi = new Float64Array(256);
        for (i = 0; i < 256; i++) {
            var k = order[i];
            lo[k] = i === 0 ? -Infinity : (tab[order[i - 1]] + tab[k]) / 2;
            hi[k] = i === 255 ? Infinity : (tab[k] + tab[order[i + 1]]) / 2;
        }
        LAW_TABS[e[0]] = { lo: lo, hi: hi };
    });
    return LAW_TABS;
}
function laplaceCdf(x, b) { return x === -Infinity ? 0 : x === Infinity ? 1 : x < 0 ? 0.5 * Math.exp(x / b) : 1 - 0.5 * Math.exp(-x / b); }
/** Mean log-likelihood of a byte histogram under the best Laplace scale for `law`. */
function lawLikelihood(hist, total, law) {
    var t = lawTables()[law], best = -Infinity;
    var offsets = law === 'u8' ? [0, -1 / 128, 1 / 128, -2 / 128, 2 / 128] : [0];   // u8 cameras sit off 0x80 by a code or two
    for (var g = 0; g < 90; g++) {
        var b = Math.exp(Math.log(1e-4) + g * (Math.log(1) - Math.log(1e-4)) / 89);
        for (var o = 0; o < offsets.length; o++) {
            var ll = 0, mu = offsets[o];
            for (var c = 0; c < 256; c++) {
                if (!hist[c]) continue;
                var pr = laplaceCdf(t.hi[c] - mu, b) - laplaceCdf(t.lo[c] - mu, b);
                ll += hist[c] * Math.log(0.995 * pr + 0.005 / 256);
            }
            if (ll / total > best) best = ll / total;
        }
    }
    return best;
}

function reader(fmt, v) {
    switch (fmt) {
        case 'u8':    return function (i) { return (v.getUint8(i) - 128) / 128; };
        case 'mulaw': return function (i) { return MULAW[v.getUint8(i)]; };
        case 'alaw':  return function (i) { return ALAW[v.getUint8(i)]; };
        case 's16le': return function (i) { return v.getInt16(i, true) / 32768; };
        case 's16be': return function (i) { return v.getInt16(i, false) / 32768; };
        case 's24le': return function (i) { return ((v.getUint8(i) | (v.getUint8(i + 1) << 8) | (v.getUint8(i + 2) << 16)) << 8 >> 8) / 8388608; };
        case 's24be': return function (i) { return ((v.getUint8(i + 2) | (v.getUint8(i + 1) << 8) | (v.getUint8(i) << 16)) << 8 >> 8) / 8388608; };
        case 's32le': return function (i) { return v.getInt32(i, true) / 2147483648; };
        case 's32be': return function (i) { return v.getInt32(i, false) / 2147483648; };
        case 'f32le': return function (i) { var x = v.getFloat32(i, true); return (x === x && x > -8 && x < 8) ? x : NaN; };
        case 'f32be': return function (i) { var x = v.getFloat32(i, false); return (x === x && x > -8 && x < 8) ? x : NaN; };
    }
}

/* Smoothness of one channel: { diff, rms, nanShare }.
 *
 * diff = Σ(Δx)² / Σ(x − mean)² over the window — the share of the signal's
 * energy that sits in sample-to-sample change. Energy-weighted, so loud audio
 * decides and a quiet hiss or digital silence does not vote; mean-removed, so a
 * wrong reading's DC offset (zeros read as u8 are a steady −1.0) cannot pass for
 * "smooth". White noise — what every WRONG reading of the bytes looks like —
 * sits at 2.0; real audio far below it.
 *
 * Measured 2026-09-25 on a raw corpus (camera ambience, music, a DAW bounce with
 * digital-silence gaps, beeps over hiss; eight formats each): the previous
 * mean|Δ|/RMS let silence and DC tip the ranking toward wrong readings with more
 * channels ("16-bit 6 ch" for a dual-mono bounce, "8-bit 8 ch" for beeps). */
function smoothness(read, spans, stride, frames) {
    var nan = 0, n = 0, sumD2 = 0, varE = 0, total = 0;
    for (var sp = 0; sp < spans.length; sp++) {
        var base = spans[sp], sum = 0, sumSq = 0, m = 0, prev = 0;
        for (var f = 0; f < frames; f++) {
            var x = read(base + f * stride);
            total++;
            if (x !== x) { nan++; continue; }
            if (m > 0) { var d = x - prev; sumD2 += d * d; }
            sum += x; sumSq += x * x; prev = x; m++;
        }
        if (m) varE += Math.max(0, sumSq - sum * sum / m);
        n += m;
    }
    var rms = Math.sqrt(varE / Math.max(1, n));
    // A constant is not audio either: a flat reading loses.
    var diff = (rms > SILENT_RMS && sumD2 > 0) ? sumD2 / varE : 9;
    return { diff: diff, rms: rms, nanShare: nan / Math.max(1, total) };
}

/* The same byte spans for every layout: SPANS pieces of SPAN_BYTES spread over
   the window, so a quiet stretch at the front cannot decide alone, and energy
   from the loud pieces carries the verdict (measured: a single 128 KB span at
   the window start sat in a DAW bounce's silence and called it noise). */
var SPANS = 4;
function spanStarts(len, shift, block) {
    var out = [], room = len - shift - SPAN_BYTES;
    if (room < 0) return [shift];
    for (var k = 0; k < SPANS; k++) {
        var at = shift + Math.floor(room * k / Math.max(1, SPANS - 1));
        out.push(at - ((at - shift) % block));
    }
    return out;
}

function scoreLayout(v, fmt, ch, shift) {
    var bps = FORMATS[fmt].bps, block = bps * ch;
    var frames = Math.floor(Math.min(SPAN_BYTES, v.byteLength - shift) / block);
    if (frames < 1024) return null;
    var read = reader(fmt, v), starts = spanStarts(v.byteLength, shift, block);
    var worst = 0, rmsMin = Infinity, nanMax = 0;
    for (var c = 0; c < ch; c++) {
        var s = smoothness(read, starts.map(function (b) { return b + c * bps; }), block, frames);
        if (s.diff > worst) worst = s.diff;
        if (s.rms < rmsMin) rmsMin = s.rms;
        if (s.nanShare > nanMax) nanMax = s.nanShare;
    }
    var score = worst + nanMax * 10;
    /* 32-bit words whose LOW half is itself a smooth signal are two 16-bit
       channels wearing a 32-bit coat. Read the low half as s16 of the same
       endianness and penalise the 32-bit story when it is audio-like. */
    if (bps === 4 && fmt.charAt(0) === 's') {
        var loRead = reader(fmt === 's32le' ? 's16le' : 's16be', v);
        var loOff = fmt === 's32le' ? 0 : 2;
        var lo = smoothness(loRead, starts.map(function (b) { return b + loOff; }), block, frames);
        if (lo.diff < AUDIO_LIKE) score += 1;
    }
    return { fmt: fmt, channels: ch, bytesPerSample: bps, blockAlign: block, shift: shift, score: score, diff: worst, rms: rmsMin };
}

/* ── Byte-order siblings ──────────────────────────────────────────────────
 * The same bytes read as s16le at shift 0 and as s16be at shift 1 share the
 * TRUE high byte of every sample, so both readings are smooth, and the
 * energy-weighted score above cannot tell them apart: measured 0.1688 against
 * 0.1688 on a dual-mono bounce (2026-10-09), where the per-window winner then
 * flipped between the two orders and split the dual-mono vote — the stereo file
 * came back as mono, which the mean|Δ|/RMS score it replaced had got right.
 * What differs is the LOW byte: the wrong reading borrows it from the
 * neighbouring sample, which adds carry noise to every block of the signal,
 * loud or quiet. So the decision between siblings is a sign test over short
 * blocks — which reading is smoother in more of them — and being scale-free it
 * cannot be drowned by a loud passage the way a pooled energy sum is. */
/* Where the sign test cannot tell — loud broadband material, where the borrowed
   low byte is as random as the true one (synthetic coloured noise at score 0.81:
   the wrong order won 48–67 % of blocks) — the prior decides for 16-bit:
   headerless PCM is little-endian far more often than not (WAV, AVI, every
   camera), so big-endian has to win clearly (true big-endian real audio: ≥ 0.95
   of blocks) and little-endian only by a plain majority. Wider samples keep the
   plain majority (see settleSiblings). */
var SIB_BLOCK = 512, SIB_MARGIN = 1.05, SIB_MAJORITY_BE = 0.8;
function deltaEnergy(read, base, stride, frames) {
    var s = 0, prev = read(base);
    for (var f = 1; f < frames; f++) { var x = read(base + f * stride), d = x - prev; s += d * d; prev = x; }
    return s;
}
/** Share of short blocks in which reading `a` is smoother than reading `b`. */
function siblingShare(view, a, b) {
    var bps = FORMATS[a.fmt].bps, block = a.blockAlign, ra = reader(a.fmt, view), rb = reader(b.fmt, view);
    var frames = Math.floor(Math.min(SPAN_BYTES, view.byteLength - Math.max(a.shift, b.shift)) / block);
    var sa = spanStarts(view.byteLength, a.shift, block), sb = spanStarts(view.byteLength, b.shift, block);
    var wins = 0, n = 0;
    for (var sp = 0; sp < Math.min(sa.length, sb.length); sp++) {
        for (var k = 0; k + SIB_BLOCK <= frames; k += SIB_BLOCK) {
            var ea = 0, eb = 0;
            for (var c = 0; c < a.channels; c++) {
                ea += deltaEnergy(ra, sa[sp] + k * block + c * bps, block, SIB_BLOCK);
                eb += deltaEnergy(rb, sb[sp] + k * block + c * bps, block, SIB_BLOCK);
            }
            if (ea !== ea || eb !== eb || ea === eb) continue;   // NaN (float) or digital silence: no vote
            n++; if (ea < eb) wins++;
        }
    }
    return n ? wins / n : 0.5;
}
/** Put the right byte order first when the top reading has a near-tied sibling. */
function settleSiblings(view, out) {
    var top = out[0];
    if (!top || FORMATS[top.fmt].bps < 2) return;
    for (var j = 1; j < out.length; j++) {
        var s = out[j];
        if (s.channels !== top.channels || FORMATS[s.fmt].bps !== FORMATS[top.fmt].bps
            || s.fmt.charAt(0) !== top.fmt.charAt(0)) continue;
        if (s.score > top.score * SIB_MARGIN + 1e-9) continue;
        /* The little-endian prior is for 16-bit, where the tie is a borrowed low byte
           and real big-endian wins ≥ 95 % of blocks. Wider samples tie the same way one
           byte up (the wrong order takes the next sample's high byte and a random middle
           byte), and there the sign test still sides with the truth but narrowly —
           55–73 % of blocks on loud and near-noise material, where the energy order
           inside the tie margin leaned the WRONG way. So for them the plain majority of
           blocks decides: the prior turned two real s24be tracks into s24le, a 0.6 bar
           left four of 27 synthetic s24be files with the energy order's wrong guess. */
        var w16 = FORMATS[top.fmt].bps === 2;
        var need = !w16 ? 0.5
                 : /be$/.test(s.fmt) && !/be$/.test(top.fmt) ? SIB_MAJORITY_BE
                 : !/be$/.test(s.fmt) && /be$/.test(top.fmt) ? 1 - SIB_MAJORITY_BE : 0.5;
        if (siblingShare(view, s, top) > need) {
            out.splice(j, 1);
            var tieScore = top.score;
            top.score = Math.max(top.score, s.score);
            s.score = tieScore;
            out.unshift(s);
        }
        return;
    }
}

/* Every byte the same value (0x00, 0x80, 0xFF…): digital silence or a
   pre-allocated, never-written file. Checked on the bytes, so it does not
   depend on which layout would have read them. */
function isFlat(view) {
    var first = view.getUint8(0), n = view.byteLength;
    for (var i = 1; i < n; i += 61) if (view.getUint8(i) !== first) return false;
    for (var j = 1; j < n; j++) if (view.getUint8(j) !== first) return false;
    return true;
}

/* Mean |pair difference| / RMS over adjacent channel pairs of a 2c reading.
   ~0 means the c-channel candidate is this file with every sample doubled. */
function pairIdentity(view, fmt, ch2, shift) {
    var bps = FORMATS[fmt].bps, block = bps * ch2;
    var frames = Math.floor(Math.min(SPAN_BYTES, view.byteLength - shift) / block);
    if (frames < 1024) return 1;
    var read = reader(fmt, view), sumD = 0, sumSq = 0, n = 0, starts = spanStarts(view.byteLength, shift, block);
    for (var g = 0; g < starts.length * frames; g++) {
        var base = starts[Math.floor(g / frames)] + (g % frames) * block, f = g;
        for (var c = 0; c + 1 < ch2; c += 2) {
            var a = read(base + c * bps), b = read(base + (c + 1) * bps);
            if (a !== a || b !== b) continue;
            sumD += Math.abs(a - b); sumSq += a * a; n++;
        }
    }
    var rms = Math.sqrt(sumSq / Math.max(1, n));
    return rms > SILENT_RMS ? (sumD / Math.max(1, n)) / rms : 1;
}

/* Frame alignment for the winning layout: the ranking only tried shifts
   inside one sample (enough to tell layouts apart); the payload may still
   start mid-frame, so try every byte position inside a frame. */
function refineShift(view, c) {
    var best = c;
    for (var shift = 0; shift < c.blockAlign; shift++) {
        if (shift === c.shift) continue;
        var s = scoreLayout(view, c.fmt, c.channels, shift);
        if (s && s.score < best.score) best = s;
    }
    return best;
}

function rank(view, fmts, chans) {
    fmts = (fmts || DEFAULT_FORMATS).filter(function (f) { return FORMATS[f]; }); chans = chans || CHANNELS;
    var out = [];
    /* Float vs int32 of the same bytes: an int32 reading of float samples is
       smooth too (equal magnitudes share exponent bits), but the reverse never
       holds — int32 audio read as float lands on exponents of 1e±38 and NaN,
       which the float reader marks. So when the float reading is valid,
       in-range and smooth, the int32 story of the same bytes is the wrong one. */
    var floatWins = {};
    fmts.forEach(function (fmt) {
        chans.forEach(function (ch) {
            var best = null;
            for (var shift = 0; shift < FORMATS[fmt].bps; shift++) {
                var s = scoreLayout(view, fmt, ch, shift);
                if (s && (!best || s.score < best.score)) best = s;
            }
            if (best) {
                out.push(best);
                if (fmt.charAt(0) === 'f' && best.score < AUDIO_LIKE) floatWins[fmt.slice(-2) + '/' + ch] = true;
            }
        });
    });
    out.forEach(function (c) { if (/^s32/.test(c.fmt) && floatWins[c.fmt.slice(-2) + '/' + c.channels]) c.score += 1; });
    out.sort(function (a, b) { return a.score - b.score; });
    /* Dual-mono: a MONO winner whose stereo reading has identical channels is
       that stereo file. Only mono → stereo: a stereo winner read as 4 channels
       shows identical pairs for the very same dual-mono file, so climbing
       further would double it again (measured: mono → stereo → 4 ch).
       Asked of the winner AND its near-tied byte-order sibling (see
       settleSiblings): only the true byte order makes the channels identical
       to the bit — the sibling's pairs differ by a borrowed low byte — so the
       exact identity settles the order where the sign test is weak (a mono
       reading of dual-mono has identical neighbours either way: 0.39–0.48). */
    var promoted = false;
    if (out.length && out[0].channels === 1 && out[0].score < AUDIO_LIKE) {
        var top = out[0], pick = null, pickId = Infinity;
        for (var m = 0; m < out.length; m++) {
            var mono = out[m];
            if (mono.channels !== 1 || FORMATS[mono.fmt].bps !== FORMATS[top.fmt].bps
                || mono.fmt.charAt(0) !== top.fmt.charAt(0) || mono.score > top.score * SIB_MARGIN + 1e-9) continue;
            var twice = null;
            for (var i = 0; i < out.length; i++) if (out[i].fmt === mono.fmt && out[i].channels === 2) { twice = out[i]; break; }
            if (!twice || twice.score >= AUDIO_LIKE) continue;
            var id = pairIdentity(view, mono.fmt, 2, twice.shift);
            if (id <= 0.02 && id < pickId) { pick = twice; pickId = id; }
        }
        if (pick) {
            out.splice(out.indexOf(pick), 1);
            pick.score = top.score; pick.dualMono = true;
            out.unshift(pick);
            promoted = true;
        }
    }
    if (!promoted) settleSiblings(view, out);
    if (out.length && out[0].channels > 1) {
        var r = refineShift(view, out[0]);
        if (r !== out[0]) { r.score = out[0].score; r.dualMono = out[0].dualMono; out[0] = r; }
    }
    return out;
}

function label(c) {
    var chan = c.channels === 1 ? 'mono' : c.channels === 2 ? 'stereo' : c.channels + ' ch';
    var endian = c.bytesPerSample > 1 ? (/be$/.test(c.fmt) ? 'big-endian' : 'little-endian') : '';
    return [FORMATS[c.fmt].label + ' PCM', chan, endian].filter(Boolean).join(' · ');
}

async function readWindow(file, at) {
    var end = Math.min(file.size, at + WINDOW_BYTES);
    return new DataView(await file.slice(at, end).arrayBuffer());
}

/**
 * @param {File|Blob} file
 * @param {{offset?:number, windows?:number, formats?:string[], channels?:number[]}} [opts]
 *        offset = where the PCM is believed to start (0 for a headerless file);
 *        windows = how many 1 MB probes, spread over the payload (default 3);
 *        formats / channels = the readings to try (default: every linear format,
 *        1/2/4/6/8 channels); knownAudio = the caller knows these bytes are
 *        audio, so a hissy reading still counts (KNOWN_AUDIO_LIKE).
 */
async function guess(file, opts) {
    opts = opts || {};
    var offset = opts.offset || 0;
    var span = Math.max(0, file.size - offset);
    var nWin = Math.max(1, Math.min(opts.windows || 3, Math.ceil(span / WINDOW_BYTES)));
    var t0 = Date.now();
    /* Windows sit strictly inside the payload, never on the first bytes: a
       torn header or a leading zero-pad would score as garbage or silence. */
    var totals = {}, order = [], silentWindows = 0, hist = new Float64Array(256), histN = 0;
    for (var w = 0; w < nWin; w++) {
        var frac = nWin === 1 ? 0.5 : (0.15 + 0.7 * w / (nWin - 1));
        var at = offset + Math.floor(span * frac);
        at -= at % 16;
        if (at + 4096 > file.size) at = Math.max(offset, file.size - 4096);
        var view = await readWindow(file, at);
        if (isFlat(view)) { silentWindows++; continue; }
        for (var hb = 0; hb < view.byteLength; hb++) hist[view.getUint8(hb)]++;
        histN += view.byteLength;
        var ranked = rank(view, opts.formats, opts.channels);
        if (!ranked.length) continue;
        ranked.forEach(function (c) {
            var k = c.fmt + '/' + c.channels;
            if (!totals[k]) { totals[k] = { sum: 0, n: 0, sample: c }; order.push(k); }
            totals[k].sum += c.score; totals[k].n++;
        });
    }
    var candidates = order.map(function (k) {
        var t = totals[k], c = Object.assign({}, t.sample);
        c.score = t.sum / t.n; c.label = label(c);
        return c;
    }).sort(function (a, b) { return a.score - b.score; });
    var best = candidates[0] || null;
    /* 8-bit winner: smoothness chose the width and channel count; the byte
       histogram chooses the law, among the laws the caller asked for. */
    var laws = (opts.formats || DEFAULT_FORMATS).filter(function (f) { return f === 'u8' || f === 'mulaw' || f === 'alaw'; });
    if (best && FORMATS[best.fmt].bps === 1 && laws.length > 1 && histN) {
        var lawScores = {}, bestLaw = null;
        laws.forEach(function (l) { lawScores[l] = lawLikelihood(hist, histN, l); });
        laws.forEach(function (l) { if (bestLaw === null || lawScores[l] > lawScores[bestLaw]) bestLaw = l; });
        if (bestLaw !== best.fmt) {
            var same = candidates.filter(function (c) { return c.fmt === bestLaw && c.channels === best.channels; })[0];
            if (same) {
                candidates.splice(candidates.indexOf(same), 1);
                same.lawChosen = true;
                candidates.unshift(same);
                best = same;
            }
        }
        best.lawScores = lawScores;
    }
    var threshold = opts.knownAudio ? KNOWN_AUDIO_LIKE : AUDIO_LIKE;
    var verdict = silentWindows === nWin ? 'silence'
        : !best ? 'noise'
        : best.score < threshold ? 'audio' : 'noise';
    return {
        verdict: verdict,
        best: verdict === 'audio' ? best : null,
        candidates: candidates.slice(0, 8),
        offset: offset,
        ms: Date.now() - t0,
        durationSec: function (rate, cand) {
            var c = cand || best; if (!c || !rate) return 0;
            return Math.floor((span - c.shift) / c.blockAlign) / rate;
        }
    };
}

window.PcmLayoutGuess = { __isStub: false, version: '1.1.0', guess: guess, AUDIO_LIKE: AUDIO_LIKE, KNOWN_AUDIO_LIKE: KNOWN_AUDIO_LIKE, _lawLikelihood: lawLikelihood, _rank: rank, _label: label };

})();
